import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { local_url } from '../Const';
import { useNavigate } from 'react-router-dom';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  width: '20%',
  [theme.breakpoints.down('sm')]: {
    padding: '3px',
    fontSize: '12px',
  },
}));

const StyledCustomerName = styled(Button)(({ theme }) => ({
  fontSize: '16px',
  padding: '6px 12px',
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: '4px',
  backgroundColor: 'transparent',
  color: theme.palette.primary.main,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
  },
}));

const FixedTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: 'calc(110vh - 300px)',
  overflow: 'auto',
}));

const FixedTableHead = styled(TableHead)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.default,
  zIndex: 1,
  '& th': {
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledOrderIdCell = styled(StyledTableCell)(({ theme }) => ({
  paddingLeft: '8px',
  paddingRight: '8px',
  fontWeight: 'bold',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: '3px',
    fontSize: '12px',
  },
}));

const CancellationTable = ({ fromDate, channelId, location, userId, toDate, onActionTaken }) => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFullAddress, setShowFullAddress] = useState({});

  // Added state for snackbar alert
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Function to handle API response
  const handleApiResponse = async (response) => {
    if (response.status === 401 || response.status === 403) {
      // Token expired - clear localStorage and redirect to login
      localStorage.clear();
      setAlertMessage('Session expired. Please login again.');
      setAlertSeverity('error');
      setAlertOpen(true);

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);

      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorText}`);
    }

    return await response.json();
  };

  // Function to show alert
  const showAlert = (message, severity = 'info') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const endpoint = `${local_url}/api/OrderStatus/GetChangeRequestList/${location}/ALL/${fromDate.toISOString().split('T')[0].replace(/-/g, '')}/${toDate.toISOString().split('T')[0].replace(/-/g, '')}?FromBranchId=ALL`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('AuthorizationToken')}`,
        }
      });

      const data = await handleApiResponse(response);
      if (data) {
        // Filter orders with requestedStatus === "CR"
        const filteredOrders = data.filter(order => order.requestedStatus === "CR");
        setOrders(filteredOrders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showAlert(`Error fetching orders: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (channelId === 'web' && location) {
      fetchOrders();
    }
  }, [channelId, location, fromDate]);

  const handleCustomerClick = (order) => {
    setSelectedOrder(order);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedOrder(null);
  };

  const handleSeeMoreClick = (uniqueId) => {
    setShowFullAddress((prev) => ({
      ...prev,
      [uniqueId]: !prev[uniqueId],
    }));
  };

  const handleHoldcancellation = async (uniqueId) => {
    try {
      const response = await fetch(
        `${local_url}/api/OrderStatus/UpdateSaleOrderCRApprovalStatus?UniqueRequestId=${uniqueId}&ApprovedByUserId=${userId}&ApprovalStatus=WH`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('AuthorizationToken')}`,
          },
        }
      );

      const result = await handleApiResponse(response);
      if (result) {
        if (result.result === 0) {
          showAlert(`Error: ${result.description || 'Invalid request'}`, 'error');
        } else {
          showAlert('Order hold successful', 'success');
          await fetchOrders(); // Refresh orders after successful hold
          if (onActionTaken) {
            onActionTaken(); // Call the parent's callback to refresh counts
          }
        }
      }
    } catch (error) {
      console.error('Error holding cancellation:', error);
      showAlert(`Error holding cancellation: ${error.message}`, 'error');
    }
  };

  const handleApproveCancellation = async (uniqueId) => {
    try {
      const response = await fetch(
        `${local_url}/api/OrderStatus/UpdateSaleOrderCRApprovalStatus?UniqueRequestId=${uniqueId}&ApprovedByUserId=${userId}&ApprovalStatus=OK`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('AuthorizationToken')}`,
          },
        }
      );

      const result = await handleApiResponse(response);
      if (result) {
        if (result.result === 0) {
          showAlert(`Error: ${result.description || 'Invalid request'}`, 'error');
        } else {
          showAlert('Cancellation approved successfully', 'success');
          await fetchOrders(); // Refresh orders after successful approval
          if (onActionTaken) {
            onActionTaken(); // Call the parent's callback to refresh counts
          }
        }
      }
    } catch (error) {
      console.error('Error approving cancellation:', error);
      showAlert(`Error approving cancellation: ${error.message}`, 'error');
    }
  };

  const handleRejectCancellation = async (uniqueId) => {
    try {
      const response = await fetch(
        `${local_url}/api/OrderStatus/UpdateSaleOrderCRApprovalStatus?UniqueRequestId=${uniqueId}&ApprovedByUserId=${userId}&ApprovalStatus=RJ`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('AuthorizationToken')}`,
          },
        }
      );

      const result = await handleApiResponse(response);
      if (result) {
        if (result.result === 0) {
          showAlert(`Error: ${result.description || 'Invalid request'}`, 'error');
        } else {
          showAlert('Cancellation rejected successfully', 'success');
          await fetchOrders(); // Refresh orders after successful rejection
          if (onActionTaken) {
            onActionTaken(); // Call the parent's callback to refresh counts
          }
        }
      }
    } catch (error) {
      console.error('Error rejecting cancellation:', error);
      showAlert(`Error rejecting cancellation: ${error.message}`, 'error');
    }
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertOpen(false);
  };

  return (
    <>
      <FixedTableContainer component={Paper} sx={{ marginTop: 2 }}>
        <Table size={isMobile ? "small" : "medium"} stickyHeader>
          <FixedTableHead>
            <TableRow>
              <StyledTableCell>Order ID</StyledTableCell>
              <StyledTableCell> Created Date</StyledTableCell>

              <StyledTableCell>Customer Name</StyledTableCell>
              <StyledTableCell>Branch</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
              {!isMobile && <StyledTableCell>Address</StyledTableCell>}
            </TableRow>
          </FixedTableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>Loading orders...</Typography>
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>No orders found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.saleOrderID}>
                  <StyledOrderIdCell>
                    <StyledCustomerName
                      onClick={() => handleCustomerClick(order)}
                      color="primary"
                      size="small"
                    >
                      {order.saleOrderID}
                    </StyledCustomerName>
                  </StyledOrderIdCell>
                  <StyledTableCell>
                    {new Date(order.createdDate).toISOString().split('T')[0].split('-').reverse().join('-')}
                  </StyledTableCell>
                  <StyledTableCell>{order.customerName}</StyledTableCell>
                  <StyledTableCell>{order.fromBranchID}</StyledTableCell>
                  <StyledTableCell>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ minWidth: '60px' }}
                          onClick={() => handleApproveCancellation(order.uniqueId)}
                          color="primary"
                        >
                          Approve
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ minWidth: '60px' }}
                          onClick={() => handleRejectCancellation(order.uniqueId)}
                          color="error"
                        >
                          Reject
                        </Button>
                      </Grid>
                      {/* <Grid item>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ minWidth: '60px' }}
                          onClick={() => handleHoldcancellation(order.uniqueId)}
                          color="warning"
                        >
                          Hold
                        </Button>
                      </Grid> */}
                    </Grid>
                  </StyledTableCell>
                  {!isMobile && (
                    <StyledTableCell>
                      <Typography variant="body2">
                        {showFullAddress[order.saleOrderID]
                          ? order.customerAddress
                          : `${order.customerAddress?.substring(0, 20) || 'N/A'}${order.customerAddress?.length > 20 ? '...' : ''}`}
                        {order.customerAddress?.length > 20 && (
                          <Button
                            size="small"
                            onClick={() => handleSeeMoreClick(order.saleOrderID)}
                          >
                            {showFullAddress[order.saleOrderID] ? 'See Less' : 'See More'}
                          </Button>
                        )}
                      </Typography>
                    </StyledTableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
          <DialogTitle>Order Details</DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <>
                <Typography variant="subtitle1">Order ID: {selectedOrder.saleOrderID}</Typography>
                <Typography variant="subtitle1">Customer Name: {selectedOrder.customerName}</Typography>
                <Typography variant="subtitle1">Branch: {selectedOrder.fromBranchID}</Typography>
                <Typography variant="subtitle1">Mobile Number: {selectedOrder.mobile}</Typography>
                <Typography variant="subtitle1">Delivery Schedule Date: {selectedOrder.deliveryScheduleDate}</Typography>
                <Typography variant="subtitle1">Time Slot: {selectedOrder.timeSlotName}</Typography>
                <Typography variant="subtitle1">Address: {selectedOrder.customerAddress || 'N/A'}</Typography>
                <Typography variant="subtitle1">Pincode: {selectedOrder.pincode || 'N/A'}</Typography>
                <Typography variant="subtitle1">Place: {selectedOrder.placeName || 'N/A'}</Typography>
                <Typography variant="subtitle1">Order Date: {new Date(selectedOrder.orderDate).toLocaleString()}</Typography>
                <Typography variant="subtitle1">Cancellation Reason: {selectedOrder.cancellationReason || 'Not specified'}</Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </FixedTableContainer>

      {/* Snackbar for displaying alerts */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertSeverity}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CancellationTable;